import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button } from "@mui/material";
import { RailStationSearchPanelProps } from "./IRailStationLookup";
import NitroFilter from "../../../components/NitroFilter";
import NitroService from "../../../utils/nitro.service";
import "./RailStationSearchPanel.css";

const RailStationSearchPanel: React.FC<RailStationSearchPanelProps> = ({
  open,
  filters,
  loading,
  isRadiusSearchFlow = false,
  radiusFilterOptions,
  onSearch,
  onClear,
  onBack,
}) => {
  const [customerName, setCustomerName] = useState(filters.customerName);
  const [stationState, setStationState] = useState(filters.stationState);
  const [address, setAddress] = useState(filters.address);
  const [zipCode, setZipCode] = useState(filters.zipCode || "");
  const [cityState, setCityState] = useState(filters.cityState);
  const [activeSuggestionField, setActiveSuggestionField] = useState<
    "customerName" | "stationState" | "address" | "zipCode" | "cityState" | null
  >(null);

  useEffect(() => {
    if (open) {
      setCustomerName(filters.customerName);
      setStationState(filters.stationState);
      setAddress(filters.address);
      setZipCode(filters.zipCode || "");
      setCityState(filters.cityState);
    }
  }, [filters, open]);

  if (!open) return null;

  const resetForm = () => {
    setCustomerName("");
    setStationState("");
    setAddress("");
    setZipCode("");
    setCityState("");
    setActiveSuggestionField(null);
    onClear();
  };

  const submitSearch = () => {
    setActiveSuggestionField(null);
    onSearch({ cityState, stationState, customerName, address, zipCode });
  };

  const trimmedCustomerName = customerName.trim();
  const trimmedStationState = stationState.trim();
  const trimmedAddress = address.trim();
  const trimmedZipCode = zipCode.trim();
  const trimmedCityState = cityState.trim();

  const hasInvalidTypedFilter =
    (trimmedCustomerName.length > 0 && trimmedCustomerName.length < 2) ||
    (trimmedStationState.length > 0 && trimmedStationState.length < 2) ||
    (trimmedAddress.length > 0 && trimmedAddress.length < 3) ||
    (trimmedZipCode.length > 0 && trimmedZipCode.length < 2);

  const hasAtLeastOneFilter =
    !!trimmedCustomerName ||
    !!trimmedStationState ||
    !!trimmedAddress ||
    !!trimmedZipCode ||
    !!trimmedCityState;
  const filledFields = [
    { key: "customerName", value: trimmedCustomerName },
    { key: "stationState", value: trimmedStationState },
    { key: "address", value: trimmedAddress },
    { key: "zipCode", value: trimmedZipCode },
    { key: "cityState", value: trimmedCityState },
  ].filter((field) => !!field.value);

  const canSubmitSearch = (() => {
    if (isRadiusSearchFlow) {
      return true;
    }

    if (!hasAtLeastOneFilter) {
      return false;
    }

    if (hasInvalidTypedFilter) {
      return false;
    }

    if (filledFields.length > 1) {
      return true;
    }

    const onlyField = filledFields[0];
    if (!onlyField) {
      return false;
    }

    if (onlyField.key === "address") {
      return onlyField.value.length >= 3;
    }

    return onlyField.value.length >= 2;
  })();

  const renderSelectField = ({
    id,
    label,
    placeholder,
    value,
    onChange,
    options,
  }: {
    id: string;
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
  }) => {
    return (
      <div className="rail-station-search-panel-field">
        <div className="rail-station-search-panel-label">
          <span>{label}</span>
          <div className="rail-station-search-panel-select-wrap">
            <select
              id={id}
              value={value}
              className={`rail-station-search-panel-select ${
                value ? "rail-station-search-panel-select-active" : ""
              }`}
              onChange={(event) => onChange(event.target.value)}
            >
              <option value="">{placeholder}</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className="rail-station-search-panel-chevron">▾</span>
          </div>
        </div>
      </div>
    );
  };

  const renderInputField = ({
    id,
    label,
    placeholder,
    value,
    onChange,
    field,
  }: {
    id: string;
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    field:
      | "customerName"
      | "stationState"
      | "address"
      | "zipCode"
      | "cityState";
  }) => {
    const isFieldActive = activeSuggestionField === field;
    const minLength = field === "address" ? 3 : 2;
    const trimmed = value.trim();
    const hasValidationError = trimmed.length > 0 && trimmed.length < minLength;
    const validationMessage =
      minLength === 3 ? "3 characters required" : "2 characters required";

    return (
      <div className="rail-station-search-panel-field">
        <div className="rail-station-search-panel-label">
          <span>{label}</span>
          {hasValidationError && (
            <span className="rail-station-search-field-error">
              {validationMessage}
            </span>
          )}
          <div className="rail-station-search-panel-select-wrap">
            <input
              id={id}
              type="text"
              value={value}
              placeholder={placeholder}
              className={`rail-station-search-panel-input ${
                isFieldActive ? "rail-station-search-panel-input-active" : ""
              } ${
                hasValidationError
                  ? "rail-station-search-panel-input-error"
                  : ""
              }`}
              onFocus={() => setActiveSuggestionField(field)}
              onChange={(event) => {
                onChange(event.target.value);
                setActiveSuggestionField(field);
              }}
              onBlur={() => {
                setTimeout(() => {
                  setActiveSuggestionField((current) =>
                    current === field ? null : current,
                  );
                }, 80);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rail-station-search-panel-overlay">
      <div className="rail-station-search-panel-shell">
        <div className="rail-station-search-panel-scroll">
          <div className="rail-station-search-panel-header-row">
            <button
              id="rail-station-search-back-btn"
              type="button"
              className="rail-carrier-review-icon-btn"
              onClick={() =>
                onBack({
                  cityState,
                  stationState,
                  customerName,
                  address,
                  zipCode,
                })
              }
              aria-label="Back"
            >
              <ArrowBackIcon fontSize="small" />
            </button>
            <h2 className="rail-station-search-panel-title">Search Stations</h2>
          </div>

          <span id="seach-station-content">
            <p className="rail-station-search-panel-helper">
              Only one field is required to search.
            </p>
            <p className="rail-station-search-panel-note">
              Enter at least 3 characters for Street Address-only searches, or
              at least 2 characters for Customer, Rail Station/State, or
              City/State searches.
            </p>

            <div className="rail-station-search-panel-fields">
              {isRadiusSearchFlow ? (
                <>
                  {renderSelectField({
                    id: "rail-station-search-customer",
                    label: "Station Name",
                    placeholder: "All Station Names",
                    value: customerName,
                    onChange: setCustomerName,
                    options: radiusFilterOptions?.stationNames || [],
                  })}

                  {renderSelectField({
                    id: "rail-station-search-station-state",
                    label: "City",
                    placeholder: "All Cities",
                    value: stationState,
                    onChange: setStationState,
                    options: radiusFilterOptions?.cities || [],
                  })}

                  {renderSelectField({
                    id: "rail-station-search-address",
                    label: "State",
                    placeholder: "All States",
                    value: address,
                    onChange: setAddress,
                    options: radiusFilterOptions?.states || [],
                  })}

                  {renderSelectField({
                    id: "rail-station-search-zip-code",
                    label: "Zip Code",
                    placeholder: "All Zipcodes",
                    value: zipCode,
                    onChange: setZipCode,
                    options: radiusFilterOptions?.zipCodes || [],
                  })}
                </>
              ) : (
                <>
                  {renderInputField({
                    id: "rail-station-search-customer",
                    label: "Customer",
                    placeholder: "Type customer",
                    value: customerName,
                    onChange: setCustomerName,
                    field: "customerName",
                  })}

                  {renderInputField({
                    id: "rail-station-search-station-state",
                    label: "Rail Station, State",
                    placeholder: "Type rail station/state",
                    value: stationState,
                    onChange: setStationState,
                    field: "stationState",
                  })}

                  {renderInputField({
                    id: "rail-station-search-address",
                    label: "Street Address",
                    placeholder: "Type street address",
                    value: address,
                    onChange: setAddress,
                    field: "address",
                  })}

                  {renderInputField({
                    id: "rail-station-search-zip-code",
                    label: "Zip Code",
                    placeholder: "Type zip code",
                    value: zipCode,
                    onChange: setZipCode,
                    field: "zipCode",
                  })}

                  <div
                    id="search-city-state"
                    className="rail-station-search-panel-field"
                  >
                    <div className="rail-station-search-panel-label">
                      <span>City, State</span>
                      <div className="rail-station-search-panel-city-state-wrap">
                        <NitroFilter
                          placeholder="Type city/state"
                          size="small"
                          data={{
                            getRecords: async (v: string) => {
                              const records =
                                await NitroService.getLocationExpanded(v);
                              return records
                                .filter(
                                  (r) =>
                                    r.type === "city" || r.type === "state",
                                )
                                .map((r) => ({
                                  label: r.name || "",
                                  value: r.name || "",
                                }));
                            },
                          }}
                          value={cityState}
                          setValue={(val: string) => {
                            setCityState(val);
                          }}
                          autocompleteOptions={{
                            filterOptions: (records: any) => records,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </span>
        </div>

        <div className="rail-station-search-panel-footer">
          <Button
            id="rail-station-search-clear-btn"
            variant="outlined"
            className="rail-station-search-panel-clear-btn"
            onClick={resetForm}
          >
            Clear
          </Button>
          <Button
            id="rail-station-search-submit-btn"
            variant="contained"
            className="rail-station-search-panel-search-btn"
            disabled={loading || !canSubmitSearch}
            onClick={submitSearch}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RailStationSearchPanel;
